import type { SetOptional } from 'type-fest'

import api from '@libs/api'

import DateUtils from '@utils/dates'
import { handleServiceError } from '@utils/errors'
import { getTimeZone } from '@utils/timezones'
import {
  IApiData,
  IBuyer,
  ICompany,
  IMatchListingData,
  IOpportunity,
  IOpportunityImport,
  IQueryFilters,
  ISolution,
  ISolutionMatch,
  ISolutionWithCompanyV2,
  OpportunityStatus,
} from '@utils/types'
import { buildQueryString } from '@utils/url'

type IFindResponse<T extends unknown> = T
const mapFindResponse = <T>(data: IFindResponse<T>) =>
  DateUtils.replaceISODateWithDate(data)

const countInactiveLeads = (): Promise<number> => {
  try {
    return api.get({
      path: '/opportunities/leads/inactive/count',
    })
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

export interface IOpportunityFunnel {
  id: string
  status: OpportunityStatus
  solution: ISolution
  createdDate?: Date
}

const findOpportunityFunnelData = async (
  queryFilters?: SetOptional<IQueryFilters, 'page' | 'limit'>
): Promise<IApiData<IOpportunityFunnel[]>> => {
  const queryString = buildQueryString(queryFilters)

  try {
    const response = (await api.get({
      path: '/opportunities/funnel',
      queryString,
    })) as IApiData<IFindResponse<IOpportunityFunnel>[]>

    return {
      ...response,
      data: response.data.map(mapFindResponse),
    }
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const findAllLeads = async (
  queryFilters?: SetOptional<IQueryFilters, 'page' | 'limit'>
): Promise<IApiData<IBuyer[]>> => {
  const queryString = buildQueryString(queryFilters)

  try {
    const response = (await api.get({
      path: '/opportunities/leads',
      queryString,
    })) as IApiData<IFindResponse<IBuyer>[]>

    return {
      ...response,
      data: response.data.map(mapFindResponse),
    }
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const findLeadByUserId = async (userId: string): Promise<IBuyer> => {
  try {
    const response = (await api.get({
      path: `/opportunities/leads/${userId}`,
    })) as IFindResponse<IBuyer>

    return mapFindResponse(response)
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const findMyVendors = async (
  queryFilters?: SetOptional<IQueryFilters, 'page' | 'limit'> & {
    withoutFavorites?: boolean
  }
): Promise<IApiData<ICompany[]>> => {
  const queryString = buildQueryString(queryFilters)

  try {
    const response = (await api.get({
      path: '/opportunities/companies',
      queryString: {
        ...queryString,
        ...(queryFilters?.withoutFavorites && {
          withoutFavorites: queryFilters.withoutFavorites || false,
        }),
      },
    })) as IApiData<ICompany[]>
    return response
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const findAllLeadsBySolution = async ({
  solutionId,
  queryFilters,
}: {
  solutionId: string
  queryFilters?: SetOptional<IQueryFilters, 'page' | 'limit'>
}): Promise<IApiData<IBuyer[]>> => {
  const queryString = buildQueryString(queryFilters)

  try {
    const response = (await api.get({
      path: `/opportunities/leads/solutions/${solutionId}`,
      queryString,
    })) as IApiData<IFindResponse<IBuyer>[]>

    return {
      ...response,
      data: response.data.map(mapFindResponse),
    }
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const cancelMatch = async ({
  opportunityId,
  entries,
  skipped,
}: {
  opportunityId: string
  entries: { question?: string; answer: string }[]
  skipped: boolean
}): Promise<IOpportunity> => {
  try {
    const response = (await api.put({
      path: `/opportunities/${opportunityId}/cancel-match`,
      data: {
        entries,
        skipped,
      },
    })) as IOpportunity
    return response
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const batchCancelMatch = async ({
  opportunityIds,
}: {
  opportunityIds: string[]
}): Promise<IOpportunity> => {
  try {
    const response = (await api.put({
      path: `/opportunities/batch/cancel-match`,
      data: {
        opportunityIds,
      },
    })) as IOpportunity
    return response
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const hideLead = async ({ leadUserId }: { leadUserId: string }) => {
  try {
    return await api.put({
      path: '/opportunities/hide-lead',
      data: {
        leadUserId,
      },
    })
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const deleteInactiveLegacyLead = async ({ leadId }: { leadId: string }) => {
  try {
    return await api.remove({
      path: `/opportunities/leads/inactive/${leadId}`,
    })
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const update = async ({
  id,
  status,
}: {
  id: string
  status: OpportunityStatus
}): Promise<void> => {
  try {
    await api.put({
      path: `/opportunities/${id}`,
      data: { status },
    })
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const importOpportunity = async ({
  opportunityToImport,
}: {
  opportunityToImport: IOpportunityImport
}): Promise<void> => {
  try {
    await api.post({
      path: '/opportunities/import',
      data: opportunityToImport,
    })
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const findAllMatchedSolutionsByBuyer = async (
  queryFilters?: SetOptional<IQueryFilters, 'page' | 'limit'>
): Promise<IApiData<ISolutionMatch[]>> => {
  const queryString = buildQueryString(queryFilters)

  try {
    const response = (await api.get({
      path: '/opportunities/matched-solutions',
      queryString,
    })) as IApiData<IFindResponse<ISolutionMatch>[]>

    return {
      ...response,
      data: response.data.map(mapFindResponse),
    }
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const findAllMatchedSolutionsByBuyer_v2 = async (
  queryFilters?: SetOptional<IQueryFilters, 'page' | 'limit'>
): Promise<IApiData<ISolutionWithCompanyV2[]>> => {
  const queryString = buildQueryString(queryFilters)

  try {
    const response = (await api.get({
      path: '/opportunities/matched-solutions-v2',
      queryString,
    })) as IApiData<IFindResponse<ISolutionWithCompanyV2>[]>

    return {
      ...response,
      data: response.data.map(mapFindResponse),
    }
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const findAllOpportunities = async (
  queryFilters?: SetOptional<IQueryFilters, 'page' | 'limit'>
): Promise<IApiData<IMatchListingData[]>> => {
  const queryString = buildQueryString(queryFilters)

  try {
    const response = (await api.get({
      path: '/opportunities',
      queryString,
    })) as IApiData<IFindResponse<IMatchListingData>[]>

    return {
      ...response,
      data: response.data.map(mapFindResponse),
    }
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const fetchCsvData = async ({
  inactiveLeadsOnly,
}: {
  inactiveLeadsOnly: boolean
}) => {
  try {
    return await api.get({
      path: '/opportunities/csv',
      queryString: {
        inactiveLeadsOnly,
        timeZone: getTimeZone(),
      },
      appendHeaders: {
        'Content-Type': 'text/csv',
        Accept: 'text/csv',
      },
    })
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const fetchCsvAdminData = async (queryFilters: IQueryFilters) => {
  const queryString = buildQueryString(queryFilters)

  try {
    const response = await api.csv({
      path: '/opportunities/admin-csv',
      queryString: {
        ...queryString,
        timeZone: getTimeZone(),
      },
    })

    return response
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const cancelMatchesWithVendor = async (id: string) => {
  try {
    await api.put({
      path: `/opportunities/companies/${id}/cancel-matches`,
    })
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const getCount = async () => {
  try {
    const response = await api.get({
      path: '/opportunities/count',
    })

    return response
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const getTotalCount = async () => {
  try {
    const response = await api.get({
      path: '/opportunities/total/count',
      authenticate: false,
    })

    return response
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

export default {
  countInactiveLeads,
  findAllLeads,
  findLeadByUserId,
  findMyVendors,
  findAllLeadsBySolution,
  findAllMatchedSolutionsByBuyer,
  findAllMatchedSolutionsByBuyer_v2,
  findAllOpportunities,
  cancelMatch,
  hideLead,
  batchCancelMatch,
  deleteInactiveLegacyLead,
  update,
  findOpportunityFunnelData,
  fetchCsvData,
  fetchCsvAdminData,
  importOpportunity,
  cancelMatchesWithVendor,
  getCount,
  getTotalCount,
}
